/**
 * CON-AUTH-001 — admin nav section definitions and breadcrumb helpers.
 *
 * Split out from `AdminLayout.tsx` so `react-refresh/only-export-components`
 * stays happy — a file with a React component export can only additionally
 * export components. Constants + pure helpers live here.
 */
import {
  AlertTriangle,
  BarChart3,
  FolderTree,
  FileText,
  Globe,
  Hammer,
  LayoutDashboard,
  Megaphone,
  MessageSquare,
  Package,
  Palette,
  Percent,
  Settings,
  ShoppingCart,
  Ticket,
  Users,
  type LucideIcon,
} from 'lucide-react';

import type { AdminSidebarNavItem } from '@dfq/shared/components/AdminSidebar';
import type { AdminBreadcrumbItem } from '@dfq/shared/components/AdminTopBar';
import type { UserRole } from '@dfq/shared/types/auth';

import { STAFF_ROLES } from '../../router/staffRoles';

/**
 * Admin nav section — extends the shared sidebar nav item with optional
 * RBAC metadata. A section is visible when EITHER (a) no gate is supplied,
 * OR (b) the user matches at least one of the declared `requiredRoles`,
 * OR (c) the user holds the `requiredPermission` string.
 */
export interface AdminNavSection extends AdminSidebarNavItem {
  /** Any of these roles grants access. */
  requiredRoles?: readonly UserRole[];
  /** Holding this permission also grants access. */
  requiredPermission?: string;
}

/**
 * Canonical admin nav with role gates layered on. The 9 base sections
 * match `ADMIN_NAV_ITEMS` from the shared capsule. Every staff role sees
 * every section while staff roles share admin permissions (PL-AUTH-120);
 * the per-section gates stay so sections can be narrowed again later.
 *
 * The icons re-import from lucide so a future change to the capsule's
 * default icons still leaves this list with the canonical admin icons.
 */
export const ADMIN_NAV_SECTIONS: readonly AdminNavSection[] = [
  {
    id: 'dashboard',
    path: '/admin',
    label: 'Dashboard',
    icon: LayoutDashboard as LucideIcon,
  },
  {
    id: 'products',
    path: '/admin/products',
    label: 'Products',
    icon: Package as LucideIcon,
  },
  {
    id: 'catalog',
    path: '/admin/categories',
    label: 'Catalog',
    icon: FolderTree as LucideIcon,
    children: [
      { id: 'categories', path: '/admin/categories', label: 'Categories' },
      { id: 'taxonomies', path: '/admin/taxonomies', label: 'Taxonomies' },
    ],
  },
  {
    id: 'orders',
    path: '/admin/orders',
    label: 'Orders',
    icon: ShoppingCart as LucideIcon,
    children: [
      { id: 'orders-all', path: '/admin/orders', label: 'All Orders' },
      {
        id: 'outstanding-balances',
        path: '/admin/orders/outstanding-balances',
        label: 'Balance Exceptions',
      },
    ],
  },
  {
    id: 'customers',
    path: '/admin/customers',
    label: 'Customers',
    icon: Users as LucideIcon,
  },
  {
    id: 'quotes',
    path: '/admin/quotes',
    label: 'Quotes',
    icon: MessageSquare as LucideIcon,
  },
  {
    id: 'custom-design-requests',
    path: '/admin/custom-design-requests',
    label: 'Design Requests',
    icon: Palette as LucideIcon,
    // The platform grants `design.requests.*` to every staff role.
    requiredRoles: STAFF_ROLES,
  },
  {
    id: 'prepress',
    path: '/admin/prepress',
    label: 'Prepress',
    icon: FileText as LucideIcon,
    requiredRoles: STAFF_ROLES,
  },
  {
    id: 'production',
    path: '/admin/production',
    label: 'Production',
    icon: Hammer as LucideIcon,
    requiredRoles: STAFF_ROLES,
  },
  {
    id: 'operations',
    path: '/admin/operations',
    label: 'Operations',
    icon: AlertTriangle as LucideIcon,
    requiredRoles: STAFF_ROLES,
    children: [
      { id: 'damage-claims', path: '/admin/damage-claims', label: 'Damage Claims' },
      { id: 'disputes', path: '/admin/disputes', label: 'Disputes' },
    ],
  },
  {
    id: 'marketing',
    path: '/admin/coupons',
    label: 'Marketing',
    icon: Megaphone as LucideIcon,
    requiredRoles: STAFF_ROLES,
    requiredPermission: 'pricing:admin',
    children: [
      { id: 'coupons', path: '/admin/coupons', label: 'Coupons', icon: Ticket as LucideIcon },
      { id: 'promotions', path: '/admin/promotions', label: 'Promotions', icon: Percent as LucideIcon },
    ],
  },
  {
    id: 'seo',
    path: '/admin/seo/redirects',
    label: 'SEO',
    icon: Globe as LucideIcon,
    requiredPermission: 'manage-seo',
    children: [{ id: 'seo-redirects', path: '/admin/seo/redirects', label: 'Redirects' }],
  },
  {
    id: 'reports',
    path: '/admin/reports',
    label: 'Reports',
    icon: BarChart3 as LucideIcon,
    requiredRoles: STAFF_ROLES,
  },
  {
    id: 'settings',
    path: '/admin/settings',
    label: 'Settings',
    icon: Settings as LucideIcon,
    requiredRoles: STAFF_ROLES,
    // CON-SETTINGS-101 — every routed `settings/*` screen (router/index.tsx)
    // gets an entry here; a route with no sidebar link is unreachable.
    children: [
      { id: 'general', path: '/admin/settings/general', label: 'General' },
      { id: 'navigation', path: '/admin/settings/navigation', label: 'Navigation' },
      { id: 'homepage', path: '/admin/settings/homepage', label: 'Homepage' },
      { id: 'artwork', path: '/admin/settings/artwork', label: 'Artwork' },
      { id: 'quotes', path: '/admin/settings/quotes', label: 'Quotes' },
      { id: 'shipping', path: '/admin/settings/shipping', label: 'Shipping' },
      { id: 'tax', path: '/admin/settings/tax', label: 'Tax' },
      { id: 'emails', path: '/admin/settings/emails', label: 'Emails' },
      { id: 'users', path: '/admin/settings/users', label: 'Users' },
      { id: 'api', path: '/admin/settings/api', label: 'API' },
      { id: 'test-orders', path: '/admin/settings/test-orders', label: 'Test orders' },
      { id: 'stripe', path: '/admin/settings/stripe', label: 'Stripe' },
      { id: 'audit-log', path: '/admin/settings/audit-log', label: 'Audit Log' },
    ],
  },
];

/**
 * Human-readable breadcrumb labels keyed by path segment. Unknown segments
 * fall back to a Title Case rendering so dynamic params (`/admin/orders/123`)
 * surface as "123" rather than an empty crumb.
 */
export const BREADCRUMB_LABELS: Readonly<Record<string, string>> = {
  admin: 'Admin',
  products: 'Products',
  orders: 'Orders',
  customers: 'Customers',
  prepress: 'Prepress',
  production: 'Production',
  operations: 'Operations',
  'damage-claims': 'Damage Claims',
  disputes: 'Disputes',
  marketing: 'Marketing',
  reports: 'Reports',
  settings: 'Settings',
  coupons: 'Coupons',
  promotions: 'Promotions',
  create: 'Create',
  edit: 'Edit',
  queue: 'Queue',
  shipping: 'Shipping',
  categories: 'Categories',
  materials: 'Materials',
  'product-types': 'Product Types',
  taxonomies: 'Taxonomies',
  vehicles: 'Vehicles',
  nodes: 'Nodes',
  import: 'Import',
  quotes: 'Quotes',
  'custom-design-requests': 'Custom Design Requests',
  sync: 'Sync',
  'pvo-import': 'PVO Import',
  mapping: 'Mapping',
  cms: 'CMS',
  pages: 'Pages',
  faq: 'FAQ',
  gallery: 'Gallery',
  testimonials: 'Testimonials',
  sales: 'Sales',
  automatic: 'Automatic',
  general: 'General',
  tax: 'Tax',
  emails: 'Emails',
  users: 'Users',
  api: 'API',
  stripe: 'Stripe',
  'audit-log': 'Audit Log',
  seo: 'SEO',
  redirects: 'Redirects',
  artwork: 'Artwork',
};

/** Title-case a kebab/snake-case path segment. */
function toTitleCase(segment: string): string {
  return segment
    .split(/[-_]/u)
    .map((part) => (part.length === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ');
}

/**
 * Turn `/admin/orders/123/edit` into a breadcrumb trail. The root `Admin`
 * crumb always links at `/admin`; interior crumbs accumulate their segment
 * path so mid-trail clicks route correctly. The final crumb has no `to`
 * so `AdminTopBar` renders it as plain text (current-page indicator).
 */
export function buildBreadcrumb(pathname: string): AdminBreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0 || segments[0] !== 'admin') {
    return [{ label: 'Admin', to: '/admin' }];
  }

  const crumbs: AdminBreadcrumbItem[] = [{ label: 'Admin', to: '/admin' }];
  let accumulated = '';
  for (let i = 1; i < segments.length; i += 1) {
    const segment = segments[i]!;
    accumulated = `${accumulated}/${segment}`;
    const isLast = i === segments.length - 1;
    const label = BREADCRUMB_LABELS[segment] ?? toTitleCase(segment);
    crumbs.push({
      label,
      to: isLast ? undefined : `/admin${accumulated}`,
    });
  }
  return crumbs;
}
